/**
 * Which roles the Settings > Users pickers offer, and which floors each role may
 * be given.
 *
 * Derived from the shared role list so a role added there reaches the pickers
 * without a hand-typed list drifting (a local list once omitted 'CX').
 */
import { userRoleSchema } from '@threadheads/shared';
import type { Location, UserRole } from '../types';

/**
 * Roles an org admin can pick, in the shared order. Super Admin is out of band
 * (the API refuses it). DTF Printer works only at floors the org runs itself, so
 * an org with none (`hidesProduction`) is not offered it.
 */
export function pickerRoles({ runsOwnFloor }: { runsOwnFloor: boolean }): UserRole[] {
  return userRoleSchema.options.filter(
    (role) => role !== 'Super Admin' && (runsOwnFloor || role !== 'DTF Printer'),
  );
}

/**
 * The floors a role can be assigned. A DTF Printer is fenced to floors the org
 * OWNS (the backend refuses junction-client and delegated floors); every other
 * role gets the list unchanged, same array reference. Delegated rows are already
 * dropped by `useUserManagementLocations`, and a shared floor carries the other
 * org as its owner, so the owner check covers both.
 */
export function floorsForRole(
  locations: Location[],
  role: UserRole,
  organizationId: string | undefined,
): Location[] {
  if (role !== 'DTF Printer') return locations;
  if (!organizationId) return [];
  return locations.filter((location) => location.owner_organization_id === organizationId);
}

export const DTF_FLOOR_HINT = 'A DTF Printer works only at floors your organization runs itself.';

/**
 * What a user form offers and submits for a role: the assignable floors, and the
 * ticked ids narrowed to them for a DTF Printer (a box ticked before the role was
 * switched must not reach the API). Other roles submit the ticked ids unchanged.
 */
export function dtfFloorPick(
  locations: Location[],
  role: UserRole,
  organizationId: string | undefined,
  selectedIds: string[],
): { floors: Location[]; submittedIds: string[]; isDtf: boolean } {
  const floors = floorsForRole(locations, role, organizationId);
  const isDtf = role === 'DTF Printer';
  if (!isDtf) return { floors, submittedIds: selectedIds, isDtf };
  const floorIds = new Set(floors.map((floor) => floor.id));
  return { floors, submittedIds: selectedIds.filter((id) => floorIds.has(id)), isDtf };
}
